<template>
    <v-card>
        <v-layout wrap>
            <v-flex xs12>
                <v-layout>
                    <v-flex xs12>
                        <h1 class="text-xs-center">Banking Details</h1>
                    </v-flex>
                </v-layout>
                <v-layout class="grid-action-buttons notPrinted" wrap>
                    <v-flex xs12 sm6 md3 offset-md3>
                        <v-btn
                            color="accent"
                            block
                            @click="exportToCSV()"
                        >
                            Export to CSV
                        </v-btn>
                    </v-flex>
                    <v-flex xs12 sm6 md3>
                        <v-btn
                            color="accent"
                            block
                            @click="triggerPrint()"
                        >
                            Print
                        </v-btn>
                    </v-flex>
                    <v-flex xs12 sm6 md3 offset-md3>
                        <v-btn
                            color="warning"
                            block
                            @click="goToCheckBankingDetails()"
                        >
                            Check Banking Details
                        </v-btn>
                    </v-flex>
                    <v-flex xs12 sm6 md3>
                        <v-btn
                            color="success"
                            block
                            @click="editBankingDetails({ Active: true })"
                        >
                            Add
                        </v-btn>
                    </v-flex>
                    <v-flex xs12 sm8 md3 offset-md3>
                        <v-text-field
                            v-model="search"
                            append-icon="search"
                            label="Filter Banking Details"
                            single-line
                            hide-details
                        ></v-text-field>
                    </v-flex>
                    <v-flex xs12 sm4 md3>
                        <v-checkbox
                            label="Active Only"
                            v-model="activeOnly"
                        ></v-checkbox>
                    </v-flex>
                </v-layout>
                <v-layout>
                    <v-flex xs12>
                        <v-data-table
                            :headers="headers"
                            :items="bankingDetails"
                            :loading="loading"
                            :search="search"
                            must-sort
                            :rows-per-page-items="rowsPerPage"
                            :pagination.sync="pagination"
                        >
                            <v-progress-linear slot="progress" color="primary" indeterminate></v-progress-linear>
                            <template slot="items" slot-scope="props">
                                <tr :data-id="props.item.ID">
                                    <td>{{props.item.Name}}</td>
                                    <td>{{props.item.ABN}}</td>
                                    <td>{{formatBSB(props.item.BSB)}}</td>
                                    <td>{{props.item.BankAccountNumber}}</td>
                                    <td v-if="props.item.Active"><v-icon color="success">check</v-icon></td>
                                    <td v-if="!props.item.Active"><v-icon color="error">close</v-icon></td>
                                    <td>
                                        <v-btn
                                            color="warning"
                                            block
                                            @click="editBankingDetails(props.item)"
                                        >
                                            Edit
                                        </v-btn>
                                    </td>
                                </tr>
                            </template>
                            <template v-if="!loading" slot="no-data">
                                <v-alert v-if="loadError" outline :value="true" color="error" icon="error">
                                    {{loadError}}
                                </v-alert>
                                <v-alert v-if="!loadError" outline :value="true" type="info">
                                    No results found
                                </v-alert>
                            </template>
                        </v-data-table>
                    </v-flex>
                </v-layout>
            </v-flex>

            <v-dialog v-model="showEditDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
                <v-card>
                    <v-toolbar dark color="primary">
                        <v-btn icon dark :disabled="saving" @click="cancelEditBankingDetails">
                            <v-icon large>close</v-icon>
                        </v-btn>
                        <v-spacer></v-spacer>
                        <v-toolbar-title v-if="editedBankingDetails && editedBankingDetails.ID">Edit Banking Details</v-toolbar-title>
                        <v-toolbar-title v-else>Add New Banking Details</v-toolbar-title>
                        <v-spacer></v-spacer>
                        <v-toolbar-items>
                            
                        </v-toolbar-items>
                    </v-toolbar>
                    <v-form ref="editForm" v-model="editBankingDetailsValid">
                        <v-layout class="dialog-content">
                            <v-flex xs12 md6 offset-md3>
                                <v-layout wrap>
                                    <v-flex xs12>
                                        <v-text-field
                                            clearable
                                            counter="100"
                                            maxlength="100"
                                            v-model="editedBankingDetails.Name"
                                            :rules="[rules.required]"
                                            label="Company Name">
                                        </v-text-field>
                                    </v-flex>
                                    <v-flex xs12>
                                        <v-text-field
                                            clearable
                                            counter="15"
                                            maxlength="15"
                                            :rules="[rules.numericOrEmpty]"
                                            v-model="editedBankingDetails.ABN" 
                                            label="ABN / ACN">
                                        </v-text-field>
                                    </v-flex>
                                    <v-flex xs12>
                                        <v-text-field
                                            clearable 
                                            :rules="[rules.bsb]"
                                            :mask="bsbMask(editedBankingDetails.BSB)"
                                            v-model="editedBankingDetails.BSB" 
                                            label="BSB">
                                        </v-text-field>
                                    </v-flex>
                                    <v-flex xs12>
                                        <v-text-field
                                            clearable
                                            counter="15"
                                            maxlength="15"
                                            :rules="[rules.numericOrEmpty]"
                                            v-model="editedBankingDetails.BankAccountNumber" 
                                            label="Bank Account Number">
                                        </v-text-field>
                                    </v-flex>
                                    <v-flex xs12>
                                        <v-checkbox label="Active" v-model="editedBankingDetails.Active"></v-checkbox>
                                    </v-flex>
                                </v-layout>
                                <v-layout wrap class="dialog-action-buttons">
                                    <v-flex xs12 sm6>
                                        <v-btn block color="error" :disabled="saving" @click="cancelEditBankingDetails">Cancel</v-btn>
                                    </v-flex>
                                    <v-flex xs12 sm6>
                                        <v-btn block color="success" :disabled="!editBankingDetailsValid" :loading="saving" @click="saveEditedBankingDetails">Save</v-btn>
                                    </v-flex>
                                    <v-flex xs12>
                                        <error-alert :content="saveError" :display="saveError"></error-alert>
                                    </v-flex>
                                </v-layout>                            
                            </v-flex>
                        </v-layout>
                    </v-form>
                </v-card>
            </v-dialog>
        </v-layout>
    </v-card>
</template>

<script>
import validationRules from "../../helpers/validationrules";
import formatters from "../../helpers/formatters";
import ErrorAlert from "../common/ErrorAlert"
import constant from "../../services/constant";

export default {
    components: {
        ErrorAlert,
    },
    data() {
        return {
            rowsPerPage: [20, 50, 100, {
                text: "All",
                value: -1
            }],
            pagination: {
                rowsPerPage: 100, // default
                sortBy: "Name"
            },
            editedBankingDetails: {},
            showEditDialog: false,
            editBankingDetailsValid: false,
            search: "",
            activeOnly: true,
            withDetailsOnly: true,
            rules: validationRules.rules,
            headers: [
                {
                    text: "Company Name",
                    value: "Name"
                },
                {
                    text: "ABN / ACN",
                    value: "ABN"
                },
                {
                    text: "BSB",
                    value: "BSB"
                },
                {
                    text: "Bank Account",
                    value: "BankAccountNumber"
                },
                {
                    text: "Active",
                    value: "Active"
                },
                {
                    text: "", // edit button
                    sortable: false
                }
            ]
        };
    },
    computed: {
        loading() {
            return this.$store.state.bankingDetailsStore.loading;
        },
        loadError() {
            return this.$store.state.bankingDetailsStore.loadError;
        },
        saving() {
            return this.$store.state.bankingDetailsStore.saving;
        },
        saveError() {
            return this.$store.state.bankingDetailsStore.saveError;
        },
        bankingDetails() {
            return this.$store.state.bankingDetailsStore.bankingDetails.filter(s => (s.Active || !this.activeOnly) && s.CompanyID == this.companyID);
        },
        companyID() {
            return this.$route.meta.companyID;
        }
    },
    methods: {
        loadBankingDetails() {
            this.$store.dispatch("bankingDetailsStore/loadBankingDetails");
        },
        editBankingDetails(item) {
            this.$store.dispatch("bankingDetailsStore/clearSaveError");

            // show which fields are mandatory when opening the edit form
            this.$refs.editForm.validate();

            this.editedBankingDetails = Object.assign({}, item);

            this.showEditDialog = true;
        },
        cancelEditBankingDetails() {
            this.showEditDialog = false;
            this.editedBankingDetails = {};
        },
        saveEditedBankingDetails() {
            var that = this;
            this.editedBankingDetails.CompanyID = this.companyID
            this.$store.dispatch("bankingDetailsStore/saveBankingDetails", {
                bankingDetails: this.editedBankingDetails,
                finally: function() {
                    if (that.$store.state.bankingDetailsStore.saveError) {
                        // do not close the dialog
                        // display the error
                    }
                    else {
                        // close the dialog
                        that.cancelEditBankingDetails();
                    }
                }
            });
        },
        bsbMask(value) {
            return validationRules.bsbMask(value);
        },
        phoneMask(value) {
            return validationRules.phoneMask(value);
        },
        formatPhoneNumber(value) {
            return formatters.formatPhoneNumber(value);
        },
        formatBSB(value) {
            return formatters.formatBSB(value);
        },
        goToCheckBankingDetails() {
            if(this.companyID == constant.CompanyID.JCG.ID) {
                this.$router.push({ name: 'checkbankingdetailsjcg'});
            }
            else if(this.companyID == constant.CompanyID.SBD.ID) {
                this.$router.push({ name: 'checkbankingdetailssbd'});
            }
            else if(this.companyID == constant.CompanyID.CRG.ID) {
                this.$router.push({ name: 'checkbankingdetailscrg'});
            }
            else if(this.companyID == constant.CompanyID.CG.ID) {
                this.$router.push({ name: 'checkbankingdetailscg'});
            }
        },
        exportToCSV() {
            /*
             * Implementation adapted from:
             * - https://stackoverflow.com/questions/8847766/how-to-convert-json-to-csv-format-and-store-in-a-variable
             * - https://github.com/mholt/PapaParse/issues/175
             * - https://codepen.io/dimaZubkov/pen/eKGdxN
             */

            /**
             * Please note the limitations of this copy method, and notes about copying arrays in JavaScript in general:
             * https://stackoverflow.com/questions/7486085/copy-array-by-value
             * 
             * We need to copy because the below function is deleting properties and modifying them - if we do this
             * directly to the objects in the original array then it changes the data that is in the banking details
             * grid, which breaks display of data and can cause issues when editing objects (like adding a new item
             * instead of editing an existing one, unexpectedly).
             */
            var data = JSON.parse(JSON.stringify(this.bankingDetails));

            data = data.map(function(item) {
                // remove columns we do not want in the exported CSV
                delete item.ID;
                
                //remove company id
                delete item.CompanyID;
                // amend some other columns to make them more user friendly
                item.Active = item.Active ? "Active" : "Inactive";

                return item;
            });

            if (!data || data.length <= 0) {
                // don't export when we have no data
                return;
            }

            // define the function to pass to strigify to handle null values (so that they are blank in the CSV instead of the word "null")
            const nullReplacer = function(key, value) {
                return value === null ? "" : value;
            }
            
            // arrays of the headers and the actual data, as strings
            var headers = Object.keys(data[0]);
            var body = data.map(function(row) {
                return headers.map(function(field) {
                    /*
                     * The appended tab character is ridiculous, but it prevents Excel from automatically
                     * formatting bank account numbers and similar data stupidly, eg 786945341523514 => 7.86945E+14
                     * 
                     * Suggestion from: https://stackoverflow.com/questions/165042/stop-excel-from-automatically-converting-certain-text-values-to-dates
                     */

                    return JSON.stringify(row[field], nullReplacer) + "\t";
                }).join(",");
            });

            // start with the body
            var content = body;

            // add headers to the start
            content.unshift(headers.join(","))

            // make it into a string
            content = content.join("\r\n");

            var csvData = new Blob([content], { type: "text/csv;charset=utf-8;"});
            var csvURL = window.URL.createObjectURL(csvData);

            // create a dummy link to the created file's contents and then pretend that we clicked it
            var dummyLink = document.createElement("a");
            dummyLink.setAttribute("href", csvURL);
            dummyLink.setAttribute("download", "banking_details_export.csv");
            dummyLink.click();
        },
        triggerPrint(){
            window.print();
        },
    },
    created() {
        this.loadBankingDetails();
    }
};
</script>

<style>
    .grid-action-buttons .flex {
        /* just prevent buttons that are side by side from touching each other */
        padding-right: 5px;
        padding-left: 5px;
    }

    .print-only {
        display: none;
    }

    @media print {
        .print-only {
            display: inherit;
        }

        .underlined-box {
            border-bottom: 1px solid black;
        }

        .details-check .flex {
            margin-top: 20px;
        }

        .details-check {
            margin-bottom: 20px;
        }
    }
</style>
